import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/auth/AuthProvider';

interface SCIMToken {
  id: string;
  name: string;
  expiresAt?: string;
  lastUsedAt?: string;
  createdAt: string;
  createdBy: string;
}

interface SCIMSettingsProps {
  orgId: string;
}

export function SCIMSettings({ orgId }: SCIMSettingsProps) {
  const { config, getAccessToken } = useAuth();
  const [tokens, setTokens] = useState<SCIMToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // New token form
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenExpiry, setTokenExpiry] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Newly created token (shown once)
  const [newToken, setNewToken] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  // SCIM endpoint info
  const scimBaseUrl = config?.api?.endpoint
    ? `${config.api.endpoint.replace('/api', '')}/scim/v2/${orgId}`
    : '';

  // API helper
  const apiCall = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${config?.api?.endpoint}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options?.headers,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'API request failed');
    }
    return data.data;
  }, [config, getAccessToken]);

  // Load tokens
  const loadTokens = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiCall(`/organizations/${orgId}/scim/tokens`);
      setTokens(data);
    } catch (err: any) {
      // Tokens endpoint might not exist yet - that's OK
      setTokens([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, orgId]);

  useEffect(() => {
    if (config?.api?.endpoint && orgId) {
      loadTokens();
    }
  }, [config, orgId]);

  // Create token
  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsCreating(true);

    try {
      const result = await apiCall(`/organizations/${orgId}/scim/tokens`, {
        method: 'POST',
        body: JSON.stringify({
          name: tokenName,
          expiresAt: tokenExpiry || undefined,
        }),
      });

      // The API returns the plain token only once
      setNewToken(result.token);
      setTokens([...tokens, result.tokenInfo]);
      setShowTokenForm(false);
      setTokenName('');
      setTokenExpiry('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  // Revoke token
  const handleRevokeToken = async (tokenId: string) => {
    if (!confirm('Are you sure you want to revoke this token? This cannot be undone.')) {
      return;
    }

    try {
      await apiCall(`/organizations/${orgId}/scim/tokens/${tokenId}`, {
        method: 'DELETE',
      });
      setTokens(tokens.filter(t => t.id !== tokenId));
      setSuccess('Token revoked successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Copy token to clipboard
  const copyToken = async () => {
    if (!newToken) return;

    try {
      await navigator.clipboard.writeText(newToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy token');
    }
  };

  // Dismiss new token display
  const dismissNewToken = () => {
    setNewToken(null);
    setTokenCopied(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Loading SCIM settings...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">SCIM Provisioning</h2>
        <p className="text-muted-foreground">
          Configure SCIM 2.0 for automated user provisioning from your identity provider.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* New Token Display */}
      {newToken && (
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-yellow-600">New SCIM Token Created</h3>
              <p className="text-sm text-muted-foreground">
                Copy this token now. It won't be shown again.
              </p>
            </div>
            <button
              onClick={dismissNewToken}
              className="text-muted-foreground hover:text-foreground"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex gap-2">
            <code className="flex-1 p-3 bg-background rounded border text-sm break-all font-mono">
              {newToken}
            </code>
            <button
              onClick={copyToken}
              className={`px-4 py-2 rounded-md ${tokenCopied ? 'bg-green-500 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
            >
              {tokenCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* SCIM Endpoint Info */}
      <div className="mb-6 p-4 bg-muted rounded-lg">
        <h3 className="text-lg font-semibold mb-4">SCIM Configuration</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              SCIM Base URL
            </label>
            <div className="flex gap-2">
              <code className="flex-1 p-2 bg-background rounded border text-sm break-all">
                {scimBaseUrl || 'Loading...'}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(scimBaseUrl)}
                className="px-3 py-2 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                title="Copy URL"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Authentication
            </label>
            <p className="text-sm">
              Use Bearer token authentication with a SCIM token from below.
            </p>
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">
              Supported Features
            </label>
            <ul className="text-sm space-y-1">
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                User provisioning (create, update, delete)
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                User filtering and pagination
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                PATCH operations
              </li>
              <li className="flex items-center gap-2 text-muted-foreground">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Groups (not supported)
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Token Management */}
      <div className="p-4 bg-muted rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">SCIM Tokens</h3>
          <button
            onClick={() => setShowTokenForm(!showTokenForm)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Generate Token
          </button>
        </div>

        {/* Create Token Form */}
        {showTokenForm && (
          <form onSubmit={handleCreateToken} className="mb-4 p-4 bg-background rounded-lg border">
            <h4 className="font-medium mb-3">Generate New Token</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="Okta SCIM Token"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A descriptive name to identify this token
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expiration (optional)</label>
                <input
                  type="date"
                  value={tokenExpiry}
                  onChange={(e) => setTokenExpiry(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty for no expiration
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                >
                  {isCreating ? 'Generating...' : 'Generate'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowTokenForm(false)}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Token List */}
        {tokens.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            No SCIM tokens. Generate one to enable provisioning.
          </p>
        ) : (
          <div className="space-y-2">
            {tokens.map((token) => (
              <div key={token.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                <div>
                  <p className="font-medium">{token.name}</p>
                  <div className="text-sm text-muted-foreground">
                    <span>Created {new Date(token.createdAt).toLocaleDateString()}</span>
                    {token.lastUsedAt && (
                      <span> &middot; Last used {new Date(token.lastUsedAt).toLocaleDateString()}</span>
                    )}
                    {token.expiresAt && (
                      <span className={new Date(token.expiresAt) < new Date() ? 'text-destructive' : ''}>
                        {' '}&middot; Expires {new Date(token.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeToken(token.id)}
                  className="px-3 py-1 text-sm bg-destructive/10 text-destructive rounded hover:bg-destructive/20"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Integration Guide */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <h4 className="font-medium text-blue-600 mb-2">Integration Guide</h4>
        <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
          <li>Generate a SCIM token above and copy it</li>
          <li>In your identity provider (Okta, Azure AD, etc.), navigate to SCIM provisioning settings</li>
          <li>Enter the SCIM Base URL shown above</li>
          <li>Use "Bearer Token" authentication and paste your SCIM token</li>
          <li>Test the connection and enable provisioning</li>
        </ol>
        <p className="text-sm text-muted-foreground mt-3">
          For detailed setup instructions for your identity provider, see our{' '}
          <a href="#" className="text-primary hover:underline">documentation</a>.
        </p>
      </div>
    </div>
  );
}
